import { connect } from 'react-redux';
import { useCallback, useContext, useEffect, useState } from 'react';

import { ApprovedTypesEnum } from 'APIPackage/KnmModule/enums/ApprovedTypesEnum';
import { approvedSelectOptions } from 'KnmErknmPackage/MainModule/constants/approvedSelectOptions';
import { AgreementsContext } from 'KnmErknmPackage/ErkmAgreementsModule/AgreementsContext';
import { KNM_APPROVE_INFO } from 'CorePackage/MainModule/routes/routes';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { Section } from 'UIPackage/FormModule/components/Section';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IErknmFormAgreement } from 'KnmErknmPackage/MainModule/interfaces/IErknmFormAgreement';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { ReactQuillEditorField } from 'UIPackage/MainModule/components/ReactQuillEditorField';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getDecisionFormRef } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { fetchAgreementApproveDate } from 'APIPackage/KnmModule/services/knmApi';
import { convertFormDateValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { parseDate } from 'CorePackage/MainModule/common/formatDate';

import { AgreementsSupportingDocumentsAttachment } from '../AgreementsSupportingDocuments/AgreementsSupportingDocumentsAttachment';
import { AgreementsProsecutorOrganization } from '../AgreementsProsecutorOrganization/AgreementsProsecutorOrganization';

interface IAgreementsApproveInfoProps {
  agreement: IErknmFormAgreement;
  id: string;
  isWatchOnly: boolean;
  name: string;
}

interface IAgreementsApproveInfoStoreProps {
  decisionFormRef: IDecisionFormRef | undefined;
  formRef: IKnmFormRef | undefined;
}

const AgreementsApproveInfoWithoutStore = ({
  agreement,
  id,
  isWatchOnly,
  name,
  decisionFormRef,
  formRef,
}: IAgreementsApproveInfoProps & IAgreementsApproveInfoStoreProps) => {
  const { isKnm, isPm, isDecision } = useContext(AgreementsContext);
  const [maxDateDecision, setMaxDateDecision] = useState<string>('');

  const fetchAgreementApproveDateAsync = useCallback(async () => {
    const response = await fetchAgreementApproveDate(convertFormDateValueToKnmDataDate(agreement.dateOnApprove) || '');

    setMaxDateDecision(response);
  }, [agreement]);

  useEffect(() => {
    fetchAgreementApproveDateAsync();
  }, []);

  /**
   *
   */
  const onApprovedChangeHandler = useCallback(() => {
    // @ts-ignore
    (isDecision ? decisionFormRef : formRef)?.current.change(`${name}.approveInfo.reasonForRefusal`);
  }, [isDecision, decisionFormRef, formRef]);

  return (
    <Section
      title={'Сведения о согласовании соглашения с органами прокуратуры'}
      id={KNM_APPROVE_INFO}
      type="group"
      border="none"
    >
      <AgreementsProsecutorOrganization isWatchOnly={true} name={name} id={id} />
      <Row>
        <FieldBlock
          id={`${id}.approveInfo.approved`}
          label={getTitleFieldWithRedStar({
            label: 'Решение по заявлению',
            field: `${name}.approveInfo.approved`,
            isKnm,
            isPm,
            isDecision,
          })}
          color="black"
          size="half"
        >
          <SelectInputField
            name={`${name}.approveInfo.approved`}
            options={approvedSelectOptions}
            size="medium"
            isClearable={false}
            disabled={isWatchOnly}
            onChange={onApprovedChangeHandler}
          />
        </FieldBlock>
        <FieldBlock size="half">
          <Row>
            <FieldBlock
              id={`${id}.approveInfo.numberDecision`}
              label={getTitleFieldWithRedStar({
                label: 'Номер решения',
                field: `${name}.approveInfo.numberDecision`,
                isKnm,
                isPm,
                isDecision,
              })}
              color="black"
              size="half"
            >
              <TextInputField name={`${name}.approveInfo.numberDecision`} disabled={isWatchOnly} />
            </FieldBlock>
            <FieldBlock
              id={`${id}.approveInfo.dateDecision`}
              label={getTitleFieldWithRedStar({
                label: 'Дата решения',
                field: `${name}.approveInfo.dateDecision`,
                isKnm,
                isPm,
                isDecision,
              })}
              color="black"
              size="half"
            >
              <DatePickerField
                name={`${name}.approveInfo.dateDecision`}
                disabled={isWatchOnly}
                minDate={parseDate({ date: agreement.dateOnApprove || '', parseFormat: DATE_FORMAT_LONG_FNS })}
                maxDate={parseDate({ date: maxDateDecision || '', parseFormat: SYSTEM_DATE_FORMAT_FNS })}
              />
            </FieldBlock>
          </Row>
        </FieldBlock>
      </Row>
      <Row>
        <FieldBlock
          id={`${id}.approveInfo.whoMadeDecision`}
          label={getTitleFieldWithRedStar({
            label: 'Кем принято решение',
            field: `${name}.approveInfo.whoMadeDecision`,
            isKnm,
            isPm,
            isDecision,
          })}
          color="black"
          size="half"
        >
          <TextInputField name={`${name}.approveInfo.whoMadeDecision`} disabled={isWatchOnly} />
        </FieldBlock>
        <FieldBlock
          id={`${id}.approveInfo.titleSigner`}
          label={getTitleFieldWithRedStar({
            label: 'Должность лица, подписавшего решение',
            field: `${name}.approveInfo.titleSigner`,
            isKnm,
            isPm,
            isDecision,
          })}
          color="black"
          size="half"
        >
          <TextInputField name={`${name}.approveInfo.titleSigner`} disabled={isWatchOnly} />
        </FieldBlock>
      </Row>

      {agreement.approveInfo?.approved?.value === ApprovedTypesEnum.DENIED && (
        <Row>
          <FieldBlock label="Мотивировка отказа (при необходимости)" color="black">
            <ReactQuillEditorField
              name={`${name}.approveInfo.reasonForRefusal`}
              placeholder="Введите мотивировку отказа"
              handleCustomChange={(e: string) => {
                // @ts-ignore
                (isDecision ? decisionFormRef : formRef)?.current?.change(`${name}.approveInfo.reasonForRefusal`, e);
              }}
              disabled={isWatchOnly}
            />
          </FieldBlock>
        </Row>
      )}

      {agreement.approveInfo?.approved?.value === ApprovedTypesEnum.APPROVED && (
        <Row>
          <FieldBlock
            id={`${id}.approveInfo.note`}
            label={getTitleFieldWithRedStar({
              label: 'Комментарий',
              field: `${name}.approveInfo.note`,
              isKnm,
              isPm,
              isDecision,
            })}
            color="black"
          >
            <ReactQuillEditorField
              name={`${name}.approveInfo.note`}
              placeholder="Введите комментарий"
              handleCustomChange={(e: string) => {
                // @ts-ignore
                (isDecision ? decisionFormRef : formRef)?.current?.change(`${name}.approveInfo.note`, e);
              }}
              disabled={isWatchOnly}
            />
          </FieldBlock>
        </Row>
      )}

      <AgreementsSupportingDocumentsAttachment
        id={`${id}.approveInfo.attachment`}
        watchOnly={isWatchOnly}
        margin="none"
        isRequired={true}
        name={`${name}.approveInfo.attachment`}
        hideButtonAdd={!!agreement.approveInfo?.attachment && agreement.approveInfo?.attachment.length > 0}
        formRef={isDecision ? decisionFormRef : formRef}
        type="one"
        title={getTitleFieldWithRedStar({
          label: 'Файл решения',
          field: `${name}.approveInfo.attachment`,
          isKnm,
          isPm,
          isDecision,
        })}
        acceptedFiles={'.pdf'}
      />
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IAgreementsApproveInfoStoreProps => ({
  formRef: getKnmFormRef(state),
  decisionFormRef: getDecisionFormRef(state),
});

export const AgreementsApproveInfo = connect(mapStateToProps)(AgreementsApproveInfoWithoutStore);
